import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import A11ySvg from 'components/A11ySvg';
import LocationPreference from 'components/LocationPreference';
import PageTitleBar from 'components/PageTitleBar';
import TextButton from 'components/TextButton';
import BattleSlotsInventory from 'components/WStoreBattleSlotsInventory';
import WStoreCategories from 'components/WStoreCategories';
import { LOCATION_DATA, PROPERTY_CHICAGO_SOUTHLAND } from 'utils/constants';
import { makeMoment } from 'utils/dates';
import { errorShape } from 'utils/shapes';
import { ReactComponent as EditLocationIcon } from 'resources/WStore_Edit_Location_Icon.svg';
import IconFilter from 'resources/Icon_Filter.png';
import WStoreIcon from 'resources/FeatureIcons/Button-WStore.svg';
import WStoreAlert from '../WStoreAlert';
import WStoreModal from '../WStoreModal';
import WStorePagination from '../WStorePagination';
import './WStoreView.css';

function renderReloadLabel({ wstoreReloadDate }) {
  if (wstoreReloadDate) {
    const momentDate = makeMoment(wstoreReloadDate);
    const labelDate = momentDate.format('h:mm A M/D/YY');

    return (
      <div className="wstore-reload-label">{'Last Reload: ' + labelDate}</div>
    );
  }
  return null;
}

function filterMenuButton(props) {
  if (props.categoryFilter.name === 'Quest') {
    props.categoryFilter.name = 'Quests';
  }

  return (
    props.categoryFilter && (
      <div className="wstore-items-filter">
        <button
          type="button"
          className="wstore-categories-filter-menu-btn"
          onClick={props.onOpenWStoreCategories}
        >
          <img src={IconFilter} alt="Open categories filter menu" />
        </button>
        <span className="category-filter-label">
          {props.categoryFilter.name}
        </span>
      </div>
    )
  );
}

function renderLocationPreference(props) {
  return (
    !!props.user && (
      <LocationPreference
        user={props.user}
        onSelectPreference={props.onSelectLocation}
        onClose={props.onCloseSelectPreference}
        currSelectedPreference={props.preferredLocation}
        show={props.showWStorePreferenceModal}
        playAnimation={true}
      />
    )
  );
}

function renderPreferredLocation(props) {
  const locationData =
    props.preferredLocation && LOCATION_DATA[props.preferredLocation];
  if (locationData) {
    const locationImage = locationData && locationData.image;
    const locationName = locationData && locationData.name;
    const locationTextClassName = locationName.split(' ')[0].toLowerCase();

    return (
      <div className="preferred-location">
        <img
          className={`preferred-location-image${
            props.editingWStorePreference ? ' empty' : ''
          }`}
          src={locationImage}
          alt={`Preferred Location: ${props.preferredLocation}`}
        />
        <span
          className={`preferred-location-property-text ${locationTextClassName}`}
        >
          {locationName}
        </span>
        <TextButton
          className="change-location"
          onClick={props.onEditLocationPreference}
        >
          <A11ySvg
            component={EditLocationIcon}
            className="location-edit-icon"
            alt="Edit location preference"
          />
        </TextButton>
      </div>
    );
  }
  return null;
}

function renderBattleSlotsInventoryModal(props) {
  const battleSlotsCategory = props.categories.filter(
    category => category.name === 'Battle Challenge'
  );

  return (
    <BattleSlotsInventory
      onClose={props.onCloseBattleSlotsInventory}
      show={props.showBattleSlotsInventory}
      onBuyMore={props.onSelectCategory}
      inventory={props.battleSlotsInventory}
      battleSlotsCategory={battleSlotsCategory}
      battleSlotsInfoText={props.battleSlotsInfoText}
    />
  );
}

function renderHeader(props) {
  const { loading, editingWStorePreference } = props;
  return (
    <PageTitleBar title="WStore" icon={WStoreIcon}>
      {renderLocationPreference(props)}
      {renderPreferredLocation(props)}
      {!loading &&
        !editingWStorePreference &&
        renderBattleSlotsInventoryModal(props)}
    </PageTitleBar>
  );
}

function renderCategoriesSelection({
  categories,
  onSelectCategory,
  onCloseWStoreCategories,
  onSelectHotPicks,
  onSelectAllCategory,
  editingWStorePreference,
  preferredLocation,
  showWStoreCategories,
  categoryFilter,
  showDiscover,
  onSelectDiscover,
  enableDiscover
}) {
  return (
    !editingWStorePreference &&
    !!categories &&
    categories.length > 0 && (
      <WStoreCategories
        show={showWStoreCategories}
        location={preferredLocation}
        categories={categories}
        onSelectHotPicks={onSelectHotPicks}
        onSelectAllCategory={onSelectAllCategory}
        onSelectCategory={onSelectCategory}
        showDiscover={showDiscover}
        enableDiscover={enableDiscover}
        onSelectDiscover={onSelectDiscover}
        onClose={onCloseWStoreCategories}
        currCategoryFilter={categoryFilter}
      />
    )
  );
}

function renderPageContent(props, scrollRef) {
  const {
    loading,
    userWScore,
    user,
    patronAssets,
    items,
    categoryFilter,
    alertMsg,
    selectedItem,
    purchaseWithTrade,
    purchaseRequested,
    purchaseConfirmation,
    purchaseResponseMsg,
    purchaseEmailMsg,
    purchaseError,
    onSelectItem,
    onSelectItemWithTrade,
    onPurchase,
    onClose,
    showWStoreCategories,
    editingWStorePreference,
    exchangeRates
  } = props;

  if (editingWStorePreference) {
    return null;
  } else if (items && items.length) {
    return (
      <React.Fragment>
        <div className="wstore-items-filter-container">
          {!loading &&
            !editingWStorePreference &&
            !showWStoreCategories &&
            filterMenuButton(props)}
        </div>
        <div className="wstore-content" ref={scrollRef}>
          <WStoreModal
            selectedItem={selectedItem}
            userWScore={userWScore}
            user={user}
            patronAssets={patronAssets}
            onPurchase={onPurchase}
            purchaseWithTrade={purchaseWithTrade}
            purchaseRequested={purchaseRequested}
            purchaseConfirmation={purchaseConfirmation}
            purchaseResponseMsg={purchaseResponseMsg}
            purchaseEmailMsg={purchaseEmailMsg}
            purchaseError={purchaseError}
            hidden={!!alertMsg}
            onClose={onClose}
            exchangeRates={exchangeRates}
          />
          <WStoreAlert
            selectedItem={selectedItem}
            alertMsg={alertMsg}
            onClose={onClose}
          />
          <WStorePagination
            patronAssets={patronAssets}
            wstoreItems={items}
            onSelectItem={onSelectItem}
            onSelectItemWithTrade={onSelectItemWithTrade}
            key={categoryFilter.value}
            exchangeRates={exchangeRates}
          />
        </div>
      </React.Fragment>
    );
  } else {
    return (
      <div className="wstore-category-empty">
        There are no items currently available in this category at the selected
        location.
        <br />
        Please check another category or location.
      </div>
    );
  }
}

function WStoreView(props) {
  const {
    loading,
    error,
    showWStoreCategories,
    propertyEnabledWStore,
    chicagoWstore,
    preferredLocation,
    categoryFilter
  } = props;
  let selectedProperty = null;
  if (propertyEnabledWStore.length) {
    selectedProperty = propertyEnabledWStore.find(
      obj => obj.property === preferredLocation
    );
  }

  const isWStoreDisabledForSelectedProperty =
    (selectedProperty && !selectedProperty.cvWStoreEnabled) ||
    (selectedProperty?.property === PROPERTY_CHICAGO_SOUTHLAND &&
      !chicagoWstore?.remoteConfigEntry?.value);

  const scrollRef = useRef(null);
  useEffect(() => scrollRef.current?.scrollTo({ top: 0 }), [categoryFilter]);
  let content;

  if (loading) {
    content = null;
  } else if (error) {
    content = (
      <div className="alert alert-danger">
        There was a problem retrieving the list of WStore items. Please try
        again later.
      </div>
    );
  } else if (isWStoreDisabledForSelectedProperty) {
    content = (
      <div className="wstore-configuration-text">
        <div>The WStore is currently unavailable.</div>
        <div>Please check back later.</div>
      </div>
    );
  } else if (
    !loading &&
    showWStoreCategories &&
    selectedProperty &&
    selectedProperty.cvWStoreEnabled
  ) {
    content = renderCategoriesSelection(props);
  } else {
    content = renderPageContent(props, scrollRef);
  }
  return (
    <div className="wstore-view">
      {renderHeader(props)}
      {renderReloadLabel(props)}
      {content}
    </div>
  );
}

WStoreView.propTypes = {
  loading: PropTypes.bool,
  error: errorShape,
  wstoreEnabled: PropTypes.bool,
  propertyEnabledWStore: PropTypes.arrayOf(PropTypes.object),
  wstoreReloadDate: PropTypes.string,
  userWScore: PropTypes.number,
  patronAssets: PropTypes.any,
  items: PropTypes.array,
  categories: PropTypes.array,
  showHotPicks: PropTypes.bool,
  onSelectItem: PropTypes.func,
  onSelectItemWithTrade: PropTypes.func,
  onSelectHotPicks: PropTypes.func,
  onSelectCategory: PropTypes.func,
  onSelectLocation: PropTypes.func,
  onPurchase: PropTypes.func,
  onClose: PropTypes.func,
  selectedItem: PropTypes.object,
  purchaseWithTrade: PropTypes.bool,
  purchaseRequested: PropTypes.bool,
  purchaseResponseMsg: PropTypes.string,
  purchaseEmailMsg: PropTypes.string,
  purchaseConfirmation: PropTypes.bool,
  purchaseError: PropTypes.bool,
  alertMsg: PropTypes.string,
  categoryFilter: PropTypes.object,
  user: PropTypes.object,
  onEditLocationPreference: PropTypes.func,
  onCloseSelectPreference: PropTypes.func,
  showWStorePreferenceModal: PropTypes.bool,
  editingWStorePreference: PropTypes.bool,
  exchangeRates: PropTypes.shape({
    wscorePerNugget: PropTypes.number,
    wscorePerRuby: PropTypes.number,
    wscorePerSapphire: PropTypes.number,
    wscorePerWChip: PropTypes.number
  })
};

export default WStoreView;
